import { useState, useEffect, useRef } from 'react';
import { Button, Input, Badge, Select, NumberInput } from '@atoms/index';
import { AlertModal } from '@organisms/index';
import { useRestaurantStore } from '@store/restaurantStore';
import { menuService } from '@services/menuService';
import type { Product, ProductIngredient } from '@services/menuService';
import { formatCurrency, parseDecimal } from '@/utils/currency';
import { cn } from '@/utils/cn';
import { apiClient } from '@services/api';
import { compressImage } from '@utils/compressImage';

interface ProductFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  product?: Product | null;
  categoryId: string;
  onSuccess: () => void;
}

/**
 * ProductFormModal - Create/edit product modal
 *
 * Per Restaurant Management Panel: Menu management
 * Follows pattern from AddAddressModal.tsx
 */
const ProductFormModal = ({
  isOpen,
  onClose,
  product,
  categoryId,
  onSuccess,
}: ProductFormModalProps) => {
  const {
    createProduct,
    updateProduct,
    uploadProductImage,
    categories,
  } = useRestaurantStore();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    imageUrl: '',
    displayOrder: '',
    tags: '',
    calories: '',
    preparationMinutes: '',
  });

  const [selectedCategoryId, setSelectedCategoryId] = useState(categoryId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alertState, setAlertState] = useState<{ isOpen: boolean; title: string; message: string }>({ isOpen: false, title: '', message: '' });
  const [error, setError] = useState<string | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [showModifierGroups, setShowModifierGroups] = useState(false);
  const [availableModifierGroups, setAvailableModifierGroups] = useState<any[]>([]);
  const [modifierGroupAssignments, setModifierGroupAssignments] = useState<
    Record<
      string,
      {
        isRequired: boolean;
        minSelection: number;
        maxSelection: number | null;
        displayOrder: number;
        optionOverrides: Record<string, { priceOverride: string | null; isEnabled: boolean }>;
      }
    >
  >({});
  const [savedProductId, setSavedProductId] = useState<string | null>(null);
  const [showIngredients, setShowIngredients] = useState(false);
  const [ingredients, setIngredients] = useState<ProductIngredient[]>([]);
  const [newIngredientName, setNewIngredientName] = useState('');
  const [isIngredientSaving, setIsIngredientSaving] = useState(false);

  const currentProductId = product?.id || savedProductId;

  // Initialize form data
  useEffect(() => {
    if (product) {
      // Handle price conversion - DECIMAL can come as string from DB
      let priceValue = 0;
      if (product.price !== null && product.price !== undefined) {
        if (typeof product.price === 'string') {
          priceValue = parseFloat(product.price) || 0;
        } else if (typeof product.price === 'number') {
          priceValue = product.price;
        }
      }

      setFormData({
        name: product.name || '',
        description: product.description || '',
        price: priceValue !== null && priceValue !== undefined ? String(priceValue) : '',
        imageUrl: product.imageUrl || '',
        displayOrder: product.displayOrder !== null && product.displayOrder !== undefined ? String(product.displayOrder) : '',
        tags: product.tags || '',
        calories: product.calories !== null && product.calories !== undefined ? String(product.calories) : '',
        preparationMinutes: product.preparationMinutes != null ? String(product.preparationMinutes) : '',
      });
      setSelectedCategoryId(product.categoryId);
    } else {
      setFormData({
        name: '',
        description: '',
        price: '',
        imageUrl: '',
        displayOrder: '',
        tags: '',
        calories: '',
        preparationMinutes: '',
      });
      setSelectedCategoryId(categoryId);
    }
  }, [product, categoryId, isOpen]);

  // Load groups when modal opens or product ID changes
  useEffect(() => {
    if (!isOpen) {
      setShowModifierGroups(false);
      setShowIngredients(false);
      setModifierGroupAssignments({});
      setIngredients([]);
      setNewIngredientName('');
      setSavedProductId(null);
      return;
    }

    const loadGroups = async () => {
      try {
        // Always load all available modifier groups first
        const modifierGroups = await menuService.getModifierGroups();
        setAvailableModifierGroups(modifierGroups);
        
        const productId = product?.id || savedProductId;
        if (productId) {
          try {
            const productWithGroups = await menuService.getProduct(productId);
            
            if (productWithGroups.modifierGroups && Array.isArray(productWithGroups.modifierGroups) && productWithGroups.modifierGroups.length > 0) {
              const assignments: typeof modifierGroupAssignments = {};
              
              productWithGroups.modifierGroups.forEach((mg: any) => {
                const optionOverrides: Record<string, { priceOverride: string | null; isEnabled: boolean }> = {};
                
                if (mg.modifierOptions && Array.isArray(mg.modifierOptions)) {
                  mg.modifierOptions.forEach((opt: any) => {
                    if (opt.priceOverride !== null || opt.priceOverride !== undefined || opt.isEnabled === false) {
                      optionOverrides[opt.id] = {
                        priceOverride: opt.priceOverride != null ? String(opt.priceOverride) : null,
                        isEnabled: opt.isEnabled ?? true,
                      };
                    }
                  });
                }
                
                assignments[mg.id] = {
                  isRequired: mg.isRequired ?? false,
                  minSelection: mg.minSelection ?? 0,
                  maxSelection: mg.maxSelection ?? null,
                  displayOrder: mg.displayOrder ?? 0,
                  optionOverrides,
                };
              });
              
              setModifierGroupAssignments(assignments);
            } else {
              setModifierGroupAssignments({});
            }
          } catch (err) {
            console.error('Failed to load product with groups:', err);
            setModifierGroupAssignments({});
          }
        } else {
          setModifierGroupAssignments({});
        }
      } catch (err) {
        console.error('Failed to load groups:', err);
      }
    };

    loadGroups();
    
    if (product?.id || savedProductId) {
      setShowModifierGroups(true);
      setShowIngredients(true);
    }
  }, [isOpen, product?.id, savedProductId]);

  useEffect(() => {
    if (!isOpen || !currentProductId) {
      setIngredients([]);
      return;
    }

    const loadIngredients = async () => {
      try {
        const loaded = await menuService.getProductIngredients(currentProductId);
        setIngredients(loaded);
      } catch (err) {
        console.error('Failed to load product ingredients:', err);
        setIngredients([]);
      }
    };

    loadIngredients();
  }, [isOpen, currentProductId]);

  const handleAddIngredient = async () => {
    if (!currentProductId) return;
    const name = newIngredientName.trim();
    if (name.length < 2) {
      setError('Malzeme adı en az 2 karakter olmalıdır');
      return;
    }

    setIsIngredientSaving(true);
    setError(null);
    try {
      const created = await menuService.createProductIngredient(currentProductId, { name });
      setIngredients((prev) => [...prev, created]);
      setNewIngredientName('');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.response?.data?.message || 'Malzeme eklenemedi');
    } finally {
      setIsIngredientSaving(false);
    }
  };

  const handleDeleteIngredient = async (ingredientId: string) => {
    setIsIngredientSaving(true);
    setError(null);
    try {
      await menuService.deleteProductIngredient(ingredientId);
      setIngredients((prev) => prev.filter((ing) => ing.id !== ingredientId));
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.response?.data?.message || 'Malzeme silinemedi');
    } finally {
      setIsIngredientSaving(false);
    }
  };

  const handleInputChange = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.files?.[0];
    if (!raw) return;
    e.target.value = '';

    const { file } = await compressImage(raw, { maxWidthOrHeight: 1200, maxSizeMB: 1.0 });

    // Immediate base64 preview from the (possibly compressed) file
    const reader = new FileReader();
    const previewUrlPromise = new Promise<string>((resolve) => {
      reader.onload = (event) => {
        const previewUrl = event.target?.result as string;
        setFormData((prev) => ({ ...prev, imageUrl: previewUrl }));
        resolve(previewUrl);
      };
    });
    reader.readAsDataURL(file);

    try {
      if (product) {
        // Update existing product image
        const result = await uploadProductImage(product.id, file);
        // Update with actual URL from server (replace base64 preview)
        if (result?.url) {
          setFormData((prev) => ({ ...prev, imageUrl: result.url }));
        }
      } else {
        const formDataData = new FormData();
        formDataData.append('file', file);
        formDataData.append('folder', 'products');

        const response = await apiClient.post<{ url: string; key: string }>(
          '/upload/image',
          formDataData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          },
        );

        // apiClient returns { success, data } structure
        if (response.data.success && response.data.data?.url) {
          // Replace base64 preview with actual server URL
          setFormData((prev) => ({ ...prev, imageUrl: response.data.data.url }));
        } else {
          setError('Resim yüklenirken bir hata oluştu');
          // Remove preview on error
          setFormData((prev) => ({ ...prev, imageUrl: '' }));
        }
      }
    } catch (err) {
      console.error('Image upload error:', err);
      setError('Resim yüklenirken bir hata oluştu');
      // Remove preview on error
      setFormData((prev) => ({ ...prev, imageUrl: '' }));
    } finally {
      // Reset input so same file can be selected again
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (savedProductId && !product) {
      return;
    }
    
    setIsSubmitting(true);
    setError(null);

    try {
      let imageUrl: string | undefined = formData.imageUrl;
      if (!imageUrl || imageUrl.trim() === '' || imageUrl.startsWith('data:')) {
        imageUrl = undefined;
      }
      
      let savedProduct: Product;
      const productId = product?.id;
      
      if (product) {
        savedProduct = await updateProduct(product.id, {
          name: formData.name,
          description: formData.description || undefined,
          price: parseFloat(formData.price) || 0,
          imageUrl,
          displayOrder: parseInt(formData.displayOrder, 10) || 0,
          tags: formData.tags || undefined,
          calories: formData.calories ? parseInt(formData.calories, 10) : undefined,
          preparationMinutes: Math.max(1, parseInt(formData.preparationMinutes, 10) || 1),
          categoryId: selectedCategoryId || undefined,
        });
      } else {
        if (!selectedCategoryId) {
          setError('Lütfen bir kategori seçin');
          setIsSubmitting(false);
          return;
        }

        const productData: any = {
          name: formData.name,
          description: formData.description || undefined,
          price: parseFloat(formData.price) || 0,
          displayOrder: parseInt(formData.displayOrder, 10) || 0,
          tags: formData.tags || undefined,
          calories: formData.calories ? parseInt(formData.calories, 10) : undefined,
          preparationMinutes: Math.max(1, parseInt(formData.preparationMinutes, 10) || 1),
        };

        if (imageUrl) {
          productData.imageUrl = imageUrl;
        }

        savedProduct = await createProduct(selectedCategoryId, productData);
      }

      const finalProductId = savedProduct?.id || productId;
      
      if (finalProductId) {
        if (!product) {
          setSavedProductId(finalProductId);
        }

        const modifierGroups = Object.entries(modifierGroupAssignments).map(
          ([modifierGroupId, assignment]) => {
            const optionOverrides = Object.entries(assignment.optionOverrides)
              .filter(([_, override]) => (override.priceOverride != null && override.priceOverride !== '') || override.isEnabled === false)
              .map(([modifierOptionId, override]) => ({
                modifierOptionId,
                priceOverride: override.priceOverride !== '' && override.priceOverride != null ? parseDecimal(override.priceOverride) : null,
                isEnabled: override.isEnabled,
              }));

            const result: any = {
              modifierGroupId,
              isRequired: assignment.isRequired,
              minSelection: assignment.minSelection,
              maxSelection: assignment.maxSelection,
              displayOrder: assignment.displayOrder,
            };

            if (optionOverrides.length > 0) {
              result.optionOverrides = optionOverrides;
            }

            return result;
          },
        );

        try {
          const assignPayload = {
            modifierGroups: modifierGroups,
          };
          console.log('Assigning modifier groups:', JSON.stringify(assignPayload, null, 2));
          await menuService.assignGroupsToProduct(finalProductId, assignPayload);
        } catch (assignError: any) {
          console.error('Failed to assign modifier groups:', assignError);
          console.error('Error response:', assignError.response?.data);
          throw assignError;
        }

        if (product) {
          onSuccess();
          onClose();
        } else {
          onSuccess();
          setShowModifierGroups(true);
        }
      }
    } catch (err: any) {
      console.error('Product save error:', err);
      setError(err.response?.data?.error?.message || 'Ürün kaydedilirken bir hata oluştu');
      setIsSubmitting(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {product ? 'Ürün Düzenle' : 'Yeni Ürün'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              ✕
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Category */}
            <div>
              <Select
                label="Kategori"
                value={selectedCategoryId}
                onChange={(value) => setSelectedCategoryId(String(value))}
                placeholder="Kategori seçin"
                required
                options={categories.map((cat) => ({
                  value: cat.id,
                  label: cat.name,
                }))}
              />
            </div>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ürün Adı *
              </label>
              <Input
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Ürün adı"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Açıklama
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Ürün açıklaması"
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Price */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fiyat (TRY) *
              </label>
              <NumberInput
                value={formData.price}
                onChange={(v) => handleInputChange('price', v)}
                placeholder="57.37"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                {formatCurrency(parseFloat(formData.price) || 0)}
              </p>
            </div>

            {/* Image */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Görsel
              </label>
              {formData.imageUrl && (
                <div className="w-full max-w-xs aspect-[4/3] rounded-lg overflow-hidden border-2 border-gray-200 bg-gray-50 flex items-center justify-center mb-2">
                  <img
                    src={formData.imageUrl}
                    alt="Ürün"
                    className="w-full h-full object-contain"
                  />
                </div>
              )}
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <Button
                type="button"
                size="sm"
                variant="primary"
                onClick={() => imageInputRef.current?.click()}
              >
                {formData.imageUrl ? 'Görseli Değiştir' : 'Görsel Yükle'}
              </Button>
            </div>

            {/* Additional Fields */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sıralama
                </label>
                <NumberInput
                  integerOnly
                  value={formData.displayOrder}
                  onChange={(v) => handleInputChange('displayOrder', v)}
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kalori
                </label>
                <NumberInput
                  integerOnly
                  value={formData.calories}
                  onChange={(v) => handleInputChange('calories', v)}
                  placeholder="500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hazırlık (dk) *
                </label>
                <NumberInput
                  integerOnly
                  value={formData.preparationMinutes}
                  onChange={(v) => handleInputChange('preparationMinutes', v)}
                  placeholder="1"
                  required
                />
              </div>
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Etiketler (virgülle ayrılmış)
              </label>
              <Input
                value={formData.tags}
                onChange={(e) => handleInputChange('tags', e.target.value)}
                placeholder="vegetarian,spicy,gluten-free"
              />
            </div>

            {/* Yan Ürün Grupları - Tüm Seçenekler (Porsiyon, Boyut, Ekstralar vb.) */}
            <div className="border-t border-gray-200 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowModifierGroups(!showModifierGroups);
                }}
                className="w-full flex items-center justify-between mb-2"
              >
                <div className="text-left">
                  <label className="block text-sm font-medium text-gray-700">
                    Yan Ürün Grupları
                  </label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Birden fazla üründe kullanılabilen yan ürün gruplarını seçin (Soslar, Ekstralar, İçecekler vb.)
                  </p>
                  {!currentProductId && Object.keys(modifierGroupAssignments).length > 0 && (
                    <p className="text-xs text-primary mt-1 font-medium">
                      ℹ️ Seçimleriniz ürün kaydedildikten sonra atanacak
                    </p>
                  )}
                </div>
                <span className="text-lg transition-transform duration-200">
                  {showModifierGroups ? '▼' : '▶'}
                </span>
              </button>

              {Object.keys(modifierGroupAssignments).length > 0 && !showModifierGroups && (
                <div className="mb-2 flex flex-wrap gap-2">
                  {availableModifierGroups
                    .filter((mg) => Object.keys(modifierGroupAssignments).includes(mg.id))
                    .map((mg) => (
                      <Badge key={mg.id} variant="info" className="text-xs">
                        {mg.name}
                      </Badge>
                    ))}
                </div>
              )}

              {showModifierGroups && (
                <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {availableModifierGroups.length === 0 ? (
                      <div className="text-center py-4">
                        <p className="text-xs text-gray-500 mb-2">
                          Henüz yan ürün grubu yok.
                        </p>
                        <p className="text-xs text-gray-400">
                          "Yan Ürünler" sekmesinden oluşturabilirsiniz.
                        </p>
                      </div>
                    ) : (
                      availableModifierGroups.map((mg) => {
                        const isSelected = Object.keys(modifierGroupAssignments).includes(mg.id);
                        const assignment = modifierGroupAssignments[mg.id] || {
                          isRequired: mg.isRequired ?? false,
                          minSelection: mg.minSelection ?? 0,
                          maxSelection: mg.maxSelection ?? null,
                          displayOrder: 0,
                          optionOverrides: {},
                        };

                        return (
                          <div
                            key={mg.id}
                            className={cn(
                              'p-3 rounded-lg border-2 transition-all',
                              isSelected
                                ? 'border-primary bg-primary/5'
                                : 'border-gray-200 bg-white'
                            )}
                          >
                            <div className="flex items-start gap-3">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setModifierGroupAssignments({
                                      ...modifierGroupAssignments,
                                      [mg.id]: {
                                        isRequired: mg.isRequired ?? false,
                                        minSelection: mg.minSelection ?? 0,
                                        maxSelection: mg.maxSelection ?? null,
                                        displayOrder: 0,
                                        optionOverrides: {},
                                      },
                                    });
                                  } else {
                                    const newAssignments = { ...modifierGroupAssignments };
                                    delete newAssignments[mg.id];
                                    setModifierGroupAssignments(newAssignments);
                                  }
                                }}
                                className="mt-0.5 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-medium text-gray-900">{mg.name}</span>
                                  {mg.isRequired && (
                                    <Badge variant="primary" className="text-xs">
                                      Zorunlu
                                    </Badge>
                                  )}
                                  {!mg.isActive && (
                                    <Badge variant="danger" className="text-xs">
                                      Pasif
                                    </Badge>
                                  )}
                                </div>
                                {mg.description && (
                                  <p className="text-xs text-gray-500 mt-1">{mg.description}</p>
                                )}
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-xs text-gray-500">
                                    {mg.modifierOptions?.length || 0} seçenek
                                  </span>
                                  {mg.minSelection > 0 && (
                                    <span className="text-xs text-gray-500">
                                      • Min: {mg.minSelection}
                                    </span>
                                  )}
                                  {mg.maxSelection && (
                                    <span className="text-xs text-gray-500">
                                      • Max: {mg.maxSelection}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {isSelected && (
                              <div className="mt-3 pt-3 border-t border-gray-200 space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="flex items-center gap-2">
                                      <input
                                        type="checkbox"
                                        checked={assignment.isRequired}
                                        onChange={(e) => {
                                          setModifierGroupAssignments({
                                            ...modifierGroupAssignments,
                                            [mg.id]: {
                                              ...assignment,
                                              isRequired: e.target.checked,
                                              minSelection: e.target.checked
                                                ? Math.max(1, assignment.minSelection)
                                                : assignment.minSelection,
                                            },
                                          });
                                        }}
                                        className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                                      />
                                      <span className="text-xs font-medium text-gray-700">
                                        Bu ürün için zorunlu
                                      </span>
                                    </label>
                                  </div>
                                    <div className="grid grid-cols-2 gap-2">
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Min Seçim (adet)
                                      </label>
                                      <NumberInput
                                        integerOnly
                                        value={assignment.minSelection != null ? String(assignment.minSelection) : ''}
                                        onChange={(s) => {
                                          const value = s === '' ? 0 : Math.max(0, parseInt(s, 10) || 0);
                                          setModifierGroupAssignments({
                                            ...modifierGroupAssignments,
                                            [mg.id]: {
                                              ...assignment,
                                              minSelection: assignment.isRequired ? Math.max(1, value) : value,
                                            },
                                          });
                                        }}
                                        className="py-2 text-sm"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Max Seçim (adet)
                                      </label>
                                      <NumberInput
                                        integerOnly
                                        value={assignment.maxSelection != null ? String(assignment.maxSelection) : ''}
                                        onChange={(s) => {
                                          const value =
                                            s === ''
                                              ? null
                                              : Math.max(
                                                  assignment.minSelection,
                                                  Math.max(0, parseInt(s, 10) || 0),
                                                );
                                          setModifierGroupAssignments({
                                            ...modifierGroupAssignments,
                                            [mg.id]: { ...assignment, maxSelection: value },
                                          });
                                        }}
                                        placeholder="∞"
                                        className="py-2 text-sm"
                                      />
                                    </div>
                                  </div>
                                </div>

                                {mg.modifierOptions && mg.modifierOptions.length > 0 && (
                                  <div className="space-y-2">
                                    <p className="text-xs font-medium text-gray-700">
                                      Seçenek fiyat farkı (₺) ve durum:
                                    </p>
                                    <div className="space-y-1 max-h-40 overflow-y-auto">
                                      {mg.modifierOptions.map((option: any) => {
                                        const optionOverride =
                                          assignment.optionOverrides[option.id] || {
                                            priceOverride: null,
                                            isEnabled: true,
                                          };

                                        return (
                                          <div
                                            key={option.id}
                                            className="flex items-center gap-2 p-2 bg-white rounded border border-gray-200"
                                          >
                                            <input
                                              type="checkbox"
                                              checked={optionOverride.isEnabled}
                                              onChange={(e) => {
                                                setModifierGroupAssignments({
                                                  ...modifierGroupAssignments,
                                                  [mg.id]: {
                                                    ...assignment,
                                                    optionOverrides: {
                                                      ...assignment.optionOverrides,
                                                      [option.id]: {
                                                        ...optionOverride,
                                                        isEnabled: e.target.checked,
                                                      },
                                                    },
                                                  },
                                                });
                                              }}
                                              className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                                            />
                                            <span className="text-xs text-gray-700 flex-1">
                                              {option.name}
                                            </span>
                                            <span className="text-xs text-gray-500 shrink-0">
                                              Grubu varsayılanı: {formatCurrency(option.priceAdjustment || 0)}
                                            </span>
                                            <NumberInput
                                              value={optionOverride.priceOverride ?? ''}
                                              onChange={(s) => {
                                                setModifierGroupAssignments({
                                                  ...modifierGroupAssignments,
                                                  [mg.id]: {
                                                    ...assignment,
                                                    optionOverrides: {
                                                      ...assignment.optionOverrides,
                                                      [option.id]: { ...optionOverride, priceOverride: s === '' ? null : s },
                                                    },
                                                  },
                                                });
                                              }}
                                              placeholder="Bu ürün için fiyat (₺)"
                                              className="w-28 py-2 text-sm"
                                            />
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                  {Object.keys(modifierGroupAssignments).length > 0 && (
                    <div className="mt-3 p-2 bg-primary/10 rounded-lg">
                      <p className="text-xs font-medium text-primary">
                        ✓ {Object.keys(modifierGroupAssignments).length} grup seçildi
                      </p>
                      {!currentProductId && (
                        <p className="text-xs text-gray-600 mt-1">
                          Bu seçimler ürün kaydedildikten sonra atanacak
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <button
                type="button"
                onClick={() => setShowIngredients((prev) => !prev)}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <span className="font-medium text-gray-900">Çıkarılabilir Malzemeler</span>
                <span className="text-sm text-gray-500">{showIngredients ? 'Gizle' : 'Göster'}</span>
              </button>
              {showIngredients && (
                <div className="p-4 space-y-3">
                  {!currentProductId ? (
                    <p className="text-sm text-gray-500">
                      Malzeme eklemek için önce ürünü kaydedin.
                    </p>
                  ) : (
                    <>
                      <div className="flex gap-2">
                        <Input
                          value={newIngredientName}
                          onChange={(e) => setNewIngredientName(e.target.value)}
                          placeholder="Örn: Soğan, Mantar"
                          maxLength={100}
                        />
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={isIngredientSaving}
                          onClick={handleAddIngredient}
                        >
                          Ekle
                        </Button>
                      </div>
                      {ingredients.length === 0 ? (
                        <p className="text-sm text-gray-500">Henüz malzeme tanımlanmadı.</p>
                      ) : (
                        <div className="space-y-2">
                          {ingredients.map((ingredient) => (
                            <div
                              key={ingredient.id}
                              className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2"
                            >
                              <span className="text-sm text-gray-900">{ingredient.name}</span>
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                disabled={isIngredientSaving}
                                onClick={() => handleDeleteIngredient(ingredient.id)}
                              >
                                Sil
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Error */}
            {error && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-3">
                <p className="text-error text-sm">{error}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4 justify-end">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={onClose}
              >
                İptal
              </Button>
              {!savedProductId || product ? (
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? 'Kaydediliyor...'
                    : product
                      ? 'Güncelle'
                      : 'Oluştur'}
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      onSuccess();
                      onClose();
                    }}
                  >
                    Tamamla
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      onSuccess();
                      onClose();
                    }}
                  >
                    Kaydet ve Kapat
                  </Button>
                </>
              )}
            </div>
          </form>
        </div>
      </div>
      <AlertModal
        isOpen={alertState.isOpen}
        title={alertState.title}
        message={alertState.message}
        variant="warning"
        onClose={() => setAlertState((a) => ({ ...a, isOpen: false }))}
      />
    </div>
  );
};

export default ProductFormModal;
